/**
 * React Query hooks for the attestation programme (P131, #483).
 *
 * Mirrors backend/framework/attestations_router.py.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { attestationKeys } from "shared/api/queryClient";
import type {
  Attestation,
  AttestationAction,
  AttestationCycle,
  AttestationCycleIn,
  AttestationIbsOption,
  AttestationIn,
} from "shared/api/types";

// ── Cycles ─────────────────────────────────────────────────────────

export function useAttestationCycles(): UseQueryResult<AttestationCycle[], ApiError> {
  return useQuery({
    queryKey: attestationKeys.cycles(),
    queryFn: () => api.get<AttestationCycle[]>("/v1/framework/attestation-cycles"),
  });
}

export function useCreateAttestationCycle(): UseMutationResult<
  AttestationCycle,
  ApiError,
  AttestationCycleIn
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body) =>
      api.post<AttestationCycle>("/v1/framework/attestation-cycles", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: attestationKeys.cycles() }),
  });
}

export function useTransitionCycle(): UseMutationResult<
  AttestationCycle,
  ApiError,
  { cycleId: string; action: "open" | "close" }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ cycleId, action }) =>
      api.post<AttestationCycle>(
        `/v1/framework/attestation-cycles/${cycleId}/transitions`,
        { action },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: attestationKeys.all }),
  });
}

// ── Attestations ───────────────────────────────────────────────────

export function useAttestations(
  cycleId: string | undefined,
): UseQueryResult<Attestation[], ApiError> {
  return useQuery({
    queryKey: attestationKeys.list(cycleId),
    queryFn: () =>
      api.get<Attestation[]>(
        `/v1/framework/attestations?cycle_id=${encodeURIComponent(cycleId ?? "")}`,
      ),
    enabled: !!cycleId,
  });
}

export function useAttestationIbsOptions(
  enabled: boolean,
): UseQueryResult<AttestationIbsOption[], ApiError> {
  return useQuery({
    queryKey: attestationKeys.ibsOptions(),
    queryFn: () =>
      api.get<AttestationIbsOption[]>("/v1/framework/attestation-ibs-options"),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCreateAttestation(): UseMutationResult<
  Attestation,
  ApiError,
  AttestationIn
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<Attestation>("/v1/framework/attestations", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: attestationKeys.all }),
  });
}

/** Edit a draft's statement. The backend has accepted this since the
 * attestation register shipped; nothing in the UI ever called it, so a
 * draft raised without a statement could never be submitted and had no
 * route to a statement either. */
export function usePatchAttestation(): UseMutationResult<
  Attestation,
  ApiError,
  { id: string; attestation_text: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ id, attestation_text }) =>
      api.patch<Attestation>(`/v1/framework/attestations/${id}`, {
        attestation_text,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: attestationKeys.all }),
  });
}

export function useTransitionAttestation(): UseMutationResult<
  Attestation,
  ApiError,
  { id: string; action: AttestationAction; comments?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ id, action, comments }) =>
      api.post<Attestation>(`/v1/framework/attestations/${id}/transitions`, {
        action,
        ...(comments !== undefined ? { comments } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: attestationKeys.all }),
  });
}
